import { STRINGS } from '@/strings/strings';

/**
 * Timer-driven progress for the story-output loading screen (Batch 6.1.1).
 *
 * Rationale: backend pipeline steps have very uneven durations (generate_story
 * alone ~5–25s), which made the previous SSE-event-driven progress bar jump
 * visibly between milestones. Dev-tunnel SSE batching compounded the problem.
 * This module owns a local easing timer that animates 0 → 95% smoothly,
 * capping at 95% until the SSE stream reports terminal completion.
 *
 * All functions here are pure. React lifecycle lives in useStoryJobStream.
 */

export type WorldSlug = 'aa' | 'kp' | 'sb';

/** Total animation window per world (ms).
 *  Day-2 smoke (2026-04-28) measured generate_story p90: AA kurz 19s /
 *  standard 31s / lang 43s; KP kurz 22s / standard 35s / lang 44s; SB
 *  Sachgeschichte 35s / Kurzerklärung 42s. AA/KP set to 35s — covers
 *  kurz + standard p90; lang-stories accept an 8–9s 95%-stall by design
 *  (UX trade-off: bar moves zügig + brief stall > bar crawls 45s).
 *  Per-duration-variant differentiation tracked in PWA #67. */
export const TIMER_TOTAL_MS: Record<WorldSlug, number> = {
  aa: 35_000,
  kp: 35_000,
  sb: 40_000,
};

export const TIMER_CAP_PERCENT = 95;
export const TIMER_TICK_MS = 100;

/**
 * Ease-out curve: 1 - (1-t)^2. Reaches ~75% of the way by 50% elapsed, then
 * slows. Feels responsive at the start and avoids "finishing too early".
 */
export function easeOut(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - clamped, 2);
}

/**
 * Compute the timer-driven percent value at a given elapsed time.
 * Caps at TIMER_CAP_PERCENT (95) once elapsedMs reaches totalMs.
 */
export function computeTimerPercent(elapsedMs: number, world: WorldSlug): number {
  const total = TIMER_TOTAL_MS[world];
  const eased = easeOut(elapsedMs / total);
  return Math.min(TIMER_CAP_PERCENT, eased * TIMER_CAP_PERCENT);
}

/**
 * Resolve the loading-screen label from the current eased percent.
 *
 * Thresholds operate on what the user sees (post-ease), not on elapsed time.
 * Copy matches the original Webflow production strings; no audio-specific
 * phase for AA/KP.
 *
 * Milestones:
 *   AA/KP: 0–30 creating  | 30–60 writing | 60–95 almostDone
 *   SB:    0–20 checking  | 20–50 researching | 50–75 writing | 75–95 almostDone
 */
export function resolveTimerLabel(percent: number, world: WorldSlug): string {
  if (world === 'sb') {
    const L = STRINGS.storyOutput.loading.timer.sb;
    if (percent < 20) return L.checking;
    if (percent < 50) return L.researching;
    if (percent < 75) return L.writing;
    return L.almostDone;
  }

  const L =
    world === 'aa' ? STRINGS.storyOutput.loading.timer.aa : STRINGS.storyOutput.loading.timer.kp;
  if (percent < 30) return L.creating;
  if (percent < 60) return L.writing;
  return L.almostDone;
}
